<template>
    <AppModal :name="name"
              :beforeClose="beforeClose"
              :beforeOpen="beforeOpen"
              width="1000"
              height="98%">

        <template v-slot:header>
            Extensions
        </template>

        <div slot="content" class="h-100 overflow-auto">
            <div class="sticky-top bg-white pb-2 pt-1">
                <div class="row mx-0">
                    <div class="col-4">
                        <AppSearchInput :value="query" @input="onSearch" class="w-100"/>
                    </div>
                    <div class="col-8">
                        <AppRadioButtonsGroup
                            label="label"
                            trackBy="key"
                            :value="selectedType"
                            :options="extensionTypes"
                            @update="onTypeSelect"/>
                    </div>
                </div>
            </div>
            <div v-if="filteredExtensions.length === 0" class="text-left text-muted mt-2 ml-3">
                No data
            </div>
            <template v-else>
                <div v-for="level in extensionLevels" class="text-left px-3 my-2">
                    <template v-if="filteredExtensions.filter(e => e.level === level.key).length !== 0">
                        <div @click="level.expand = !level.expand" class="extension-level-header mt-4">
                            {{ level.label }}
                            <AppExpandChevron :show="level.expand"/>
                        </div>
                        <div v-if="level.expand">
                            <FormExtensionsItem
                                v-for="extension in filteredExtensions.filter(e => e.level === level.key)"
                                :key="extension.name"
                                @toggle="toggleExtension(extension)"
                                :settings="settings"
                                :extension="extension">
                                <template v-slot:footer="{ enabled }">
                                    <FormExtensionParams
                                        @update="({path, value}) => onExtensionParamUpdate({extension, path, value})"
                                        :enabled="enabled"
                                        :extension="extension"
                                        class="mt-2"/>
                                </template>
                            </FormExtensionsItem>
                        </div>
                    </template>
                </div>
            </template>
        </div>

        <template slot="actions">
            <div class="row mx-0">
                <div class="col align-self-center text-left">
                    <div class="custom-control custom-checkbox">
                        <input v-model="showAvailableOnly"
                               type="checkbox"
                               class="custom-control-input"
                               id="showAvailableOnly">
                        <label class="custom-control-label pt-1" for="showAvailableOnly">
                            Show available only
                        </label>
                    </div>
                </div>
                <div class="col align-self-center text-right">
                    <button @click="close" class="btn btn-primary mt-0">
                        Ok
                    </button>
                </div>
            </div>
        </template>

    </AppModal>
</template>

<script>
import {
    FormExtensionLevel,
    FormExtensionType,
    getFormExtensionByName,
    getFormExtensionsList
} from '@/types/document-viewer/extension'
import AppExpandChevron from '../../../../../components/shared/AppExpandChevron'
import FormExtensionsItem from './FormExtensionsItem'
import AppRadioButtonsGroup from '../../../../../components/shared/AppRadioButtonsGroup'
import AppSearchInput from '../../../../../components/shared/AppSearchInput'
import {mapGetters} from 'vuex'
import FormExtensionParams from './FormExtensionParams'

export default {
    name: 'FormExtensions',
    components: {FormExtensionParams, AppSearchInput, AppRadioButtonsGroup, FormExtensionsItem, AppExpandChevron},
    props: {
        name: {
            type: String,
            required: true
        },
        extensions: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            query: '',
            showAvailableOnly: false,

            selectedType: 'ALL',
            extensionTypes: [{
                label: 'All',
                key: 'ALL'
            }, {
                label: 'Calculate',
                key: FormExtensionType.CALCULATION
            }, {
                label: 'Confirm',
                key: FormExtensionType.CONFIRMATION
            }, {
                label: 'Validate',
                key: FormExtensionType.VALIDATION
            }, {
                label: 'Component',
                key: FormExtensionType.COMPONENT
            }, {
                label: 'Utility',
                key: FormExtensionType.UTILITY
            }],

            extensionLevels: {
                field: {
                    label: 'Field',
                    key: FormExtensionLevel.FIELD,
                    expand: true
                },
                section: {
                    label: 'Section',
                    key: FormExtensionLevel.SECTION,
                    expand: true
                },
                form: {
                    label: 'Form',
                    key: FormExtensionLevel.FORM,
                    expand: true
                }
            }
        }
    },
    computed: {
        ...mapGetters('doc_viewer_settings', [
            'checkIsAvailableExtension'
        ]),

        filteredExtensions() {
            return getFormExtensionsList()
                .filter(e => (this.selectedType === 'ALL' ? true : e.type === this.selectedType) &&
                    (
                        (typeof e.label === 'string' && e.label.toLowerCase().includes(this.query.toLowerCase())) ||
                        (typeof e.tooltip === 'string' && e.tooltip.toLowerCase().includes(this.query.toLowerCase()))
                    ) &&
                    (!this.showAvailableOnly ? true : this.checkIsAvailableExtension(e.name))
                )
                .map(e => {
                    const extensionData = this.extensions.find(extension => extension.name === e.name)
                    if (extensionData) {
                        return {...e, ...extensionData}
                    } else {
                        return e
                    }
                })
        }
    },
    methods: {
        beforeOpen({params}) {
        },

        beforeClose() {
        },

        close() {
            this.$modal.hide(this.name)
        },

        onChange(v) {
        },

        toggleExtension(extension) {
            const extensions = _.cloneDeep(this.extensions)
            const idx = extensions.findIndex(e => e.name === extension.name)
            if (idx === -1) {
                const data = {name: extension.name}
                if (extension.params) {
                    data.params = extension.params
                }
                extensions.push(data)
            } else {
                extensions.splice(idx, 1)
            }
            this.$emit('update', extensions)
        },

        onExtensionParamUpdate({extension, path, value}) {
            const extensions = _.cloneDeep(this.extensions)
            const idx = extensions.findIndex(e => e.name === extension.name)
            if (idx !== -1) {
                if (!extensions[idx].params) {
                    const extensionModel = getFormExtensionByName(extensions[idx].name)
                    if (extensionModel && _.isObject(extensionModel.params)) {
                        extensions[idx].params = _.cloneDeep(extensionModel.params)
                    } else {
                        extensions[idx].params = {}
                    }
                }
                _.set(extensions[idx].params, path, value)
            }
            this.$emit('update', extensions)
        },

        onTypeSelect(type) {
            this.selectedType = type
        },

        onSearch(e) {
            this.query = e.target.value
        }
    }
}
</script>

<style scoped lang="scss">
.extension-level-header {
    font-size: 18px;
    font-weight: 500;

    &:hover {
        cursor: pointer;
    }
}
</style>
